import React, { useState, useCallback, useMemo } from "react";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

// Formatting helpers (unchanged)
const fmt = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);

const fmtD = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v || 0);

const pct = (v) => `${(v * 100).toFixed(2)}%`;

function pmt(rate, nper, pv) {
  if (rate === 0) return pv / nper;
  const r = rate / 12;
  return (pv * r * Math.pow(1 + r, nper * 12)) / (Math.pow(1 + r, nper * 12) - 1);
}

// Tooltip definitions (unchanged)
const TOOLTIP_FORMULAS = {
  // ... (all existing tooltips unchanged for brevity, keeping them as they were)
  commissions: { label: "Commissions", formula: "Purchase Price × 3%", vars: (s) => ({ "Purchase Price": fmt(s.purchasePrice), Rate: "3%", Result: fmt(s.purchasePrice * 0.03) }) },
  assignmentFee: { label: "Assignment Fee", formula: "(Monthly Net Income ÷ 100) × $2,000", vars: (s, c) => ({ "Monthly Net Income": fmt(c.monthlyNetIncome), "Per $100 Cash Flow": "$2,000", Result: fmt(c.assignmentFee) }) },
  totalAcquisition: { label: "Total Acquisition", formula: "Purchase Price + Commissions + Closing Fees + Assignment Fee + Improvements + Other", vars: (s, c) => ({ "Purchase Price": fmt(s.purchasePrice), Commissions: fmt(s.commissions), "Closing Fees": fmt(s.closingFees), "Assignment Fee": fmt(c.assignmentFee), Improvements: fmt(s.improvements), Other: fmt(s.other), Result: fmt(c.totalAcquisition) }) },
  entryFee: { label: "Entry Fee (Cash)", formula: "Down Payment + Commissions + Closing Fees + Assignment Fee + Improvements + Other − Private Loan 2", vars: (s, c) => ({ "Down Payment": fmt(s.downPayment), Commissions: fmt(c.commissions), "Closing Fees": fmt(s.closingFees), "Assignment Fee": fmt(c.assignmentFee), Improvements: fmt(s.improvements), Other: fmt(s.other), "Private Loan 2": fmt(s.privateLoan2), Result: fmt(c.entryFee) }) },
  monthlyPmt: { label: "Monthly Payment (P&I)", formula: "PMT(interestRate/12, termYears×12, loanAmount) — standard amortization", vars: (s, c) => ({ "Loan Amount": fmt(c.sellerLoan1), "Interest Rate": pct(s.interestRate1 / 100), "Term (years)": s.termYears1, Result: fmtD(c.monthlyPmt1) }) },
  totalDebtMonthly: { label: "Total Debt Service (Monthly)", formula: "Monthly Pmt Loan 1 + Monthly Pmt Loan 2", vars: (s, c) => ({ "Loan 1 Monthly": fmtD(c.monthlyPmt1), "Loan 2 Monthly": fmtD(c.monthlyPmt2), Result: fmt(c.totalDebtMonthly) }) },
  totalDebtAnnual: { label: "Total Debt Service (Annual)", formula: "Total Debt Service Monthly × 12", vars: (s, c) => ({ Monthly: fmt(c.totalDebtMonthly), Result: fmt(c.totalDebtAnnual) }) },
  managementFee: { label: "Management Fee", formula: "Gross Rent × Management Fee %", vars: (s) => ({ "Gross Rent": fmt(s.grossRent), Rate: pct(s.mgmtPct / 100), Result: fmtD((s.grossRent * s.mgmtPct) / 100) }) },
  maintenanceReserve: { label: "Maintenance Reserve", formula: "Gross Rent × Maintenance %", vars: (s) => ({ "Gross Rent": fmt(s.grossRent), Rate: pct(s.maintPct / 100), Result: fmtD((s.grossRent * s.maintPct) / 100) }) },
  insurance: { label: "Insurance", formula: "Gross Rent × Insurance %", vars: (s) => ({ "Gross Rent": fmt(s.grossRent), Rate: pct(s.insurancePct / 100), Result: fmtD((s.grossRent * s.insurancePct) / 100) }) },
  propTax: { label: "Property Taxes", formula: "Gross Rent × Property Tax %", vars: (s) => ({ "Gross Rent": fmt(s.grossRent), Rate: pct(s.propTaxPct / 100), Result: fmtD((s.grossRent * s.propTaxPct) / 100) }) },
  vacancy: { label: "Vacancy Reserve", formula: "Gross Rent × Vacancy %", vars: (s) => ({ "Gross Rent": fmt(s.grossRent), Rate: pct(s.vacancyPct / 100), Result: fmtD((s.grossRent * s.vacancyPct) / 100) }) },
  noi: { label: "Net Operating Income (Monthly)", formula: "Gross Rent − All Expenses", vars: (s, c) => ({ "Gross Rent": fmt(s.grossRent), "Total Expenses": fmt(c.totalExpenses), Result: fmt(c.noi) }) },
  annualNoi: { label: "Annual NOI", formula: "Monthly NOI × 12", vars: (s, c) => ({ "Monthly NOI": fmt(c.noi), Result: fmt(c.annualNoi) }) },
  purchaseCapRate: { label: "Purchase Cap Rate", formula: "Annual NOI ÷ Total Acquisition", vars: (s, c) => ({ "Annual NOI": fmt(c.annualNoi), "Total Acquisition": fmt(c.totalAcquisition), Result: pct(c.purchaseCapRate) }) },
  marketCapValue: { label: "Market Cap Value", formula: "Annual NOI ÷ Market Cap Rate", vars: (s, c) => ({ "Annual NOI": fmt(c.annualNoi), "Market Cap Rate": pct(s.marketCapRate / 100), Result: fmt(c.marketCapValue) }) },
  cashOnCash: { label: "Cash-on-Cash ROI", formula: "Annual Net Income ÷ Entry Fee (Cash)", vars: (s, c) => ({ "Annual Net Income": fmt(c.annualNetIncome), "Entry Fee (Cash)": fmt(c.entryFee), Result: pct(c.cashOnCash) }) },
  depreciation: { label: "Depreciation Write-off", formula: "(Total Acquisition − Land Value) ÷ 27.5", vars: (s, c) => ({ "Total Acquisition": fmt(c.totalAcquisition), "Land Value": fmt(s.landValue), Years: "27.5", Result: fmt(c.depreciation) }) },
  monthlyNetIncome: { label: "Monthly Net Income", formula: "NOI − Total Debt Service", vars: (s, c) => ({ "Monthly NOI": fmt(c.noi), "Total Debt Service": fmt(c.totalDebtMonthly), Result: fmt(c.monthlyNetIncome) }) },
  annualNetIncome: { label: "Annual Net Income", formula: "Monthly Net Income × 12", vars: (s, c) => ({ "Monthly Net Income": fmt(c.monthlyNetIncome), Result: fmt(c.annualNetIncome) }) },
};

// Styled tooltip & helper components (unchanged)
const StyledTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} arrow placement="bottom" />)({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "white",
    color: "#1e293b",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    border: "0.5px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "11px",
    maxWidth: "280px",
  },
  "& .MuiTooltip-arrow": {
    color: "white",
    "&::before": { border: "0.5px solid #e2e8f0" },
  },
});

function FormulaCell({ id, value, computed, inputs }) {
  const def = TOOLTIP_FORMULAS[id];
  if (!def) return <span className="text-sm font-medium text-gray-900">{value}</span>;
  const vars = def.vars(inputs, computed);
  const tooltipContent = (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-xs font-semibold text-gray-800">{def.label}</span>
      </div>
      <div className="bg-gray-50 rounded-md p-2 text-[11px] font-mono text-gray-600 border border-gray-100">{def.formula}</div>
      <div className="flex flex-col gap-1">
        {Object.entries(vars).map(([k, v]) => (
          <div key={k} className="flex justify-between text-[11px]">
            <span className="text-gray-500">{k}</span>
            <span className={k === "Result" ? "font-medium text-blue-700" : "text-gray-800"}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <StyledTooltip title={tooltipContent} enterDelay={300} leaveDelay={100}>
      <button className="bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-sm font-medium text-blue-700 flex items-center gap-1 hover:bg-blue-100 transition-colors" type="button">
        {value}
      </button>
    </StyledTooltip>
  );
}

function Input({ label, value, onChange, prefix, suffix, type = "number", step, min = 0 }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-100">
      <label className="text-xs text-gray-600 flex-shrink-0 max-w-[55%]">{label}</label>
      <div className="flex items-center gap-1">
        {prefix && <span className="text-xs text-gray-400">{prefix}</span>}
        <input type={type} value={value} min={min} step={step || 1} onChange={(e) => onChange(type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)} className="w-[90px] text-right text-xs py-1 px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" />
        {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
}

function LinkedAmountInput({ label, baseValue, amount, onAmountChange, step = 1000, min = 0 }) {
  const percent = baseValue > 0 ? (amount / baseValue) * 100 : 0;
  const handleDollarChange = (newDollar) => {
    const dollarValue = parseFloat(newDollar) || 0;
    onAmountChange(dollarValue);
  };
  const handlePercentChange = (newPercent) => {
    const percentValue = parseFloat(newPercent) || 0;
    const newDollar = (percentValue / 100) * baseValue;
    onAmountChange(newDollar);
  };
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-100">
      <label className="text-xs text-gray-600 flex-shrink-0">{label}</label>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">$</span>
          <input type="number" value={amount} min={min} step={step} onChange={(e) => handleDollarChange(e.target.value)} className="w-[90px] text-right text-xs py-1 px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" />
        </div>
        <span className="text-xs text-gray-400">–</span>
        <div className="flex items-center gap-1">
          <input type="number" value={percent.toFixed(1)} min={0} max={100} step={0.5} onChange={(e) => handlePercentChange(e.target.value)} className="w-12 text-right text-xs py-1 px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" />
          <span className="text-xs text-gray-400">%</span>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, color }) {
  return (
    <div className="text-white text-[11px] font-medium tracking-wide uppercase rounded-md px-2.5 py-1 mt-3 mb-1.5" style={{ backgroundColor: color }}>
      {title}
    </div>
  );
}

function ResultRow({ label, value, id, computed, inputs, muted, percentage }) {
  let content;
  if (percentage !== undefined) {
    content = (
      <span className="text-sm font-medium text-gray-900">
        {value} <span className="text-xs text-gray-500">({pct(percentage)})</span>
      </span>
    );
  } else if (id && computed && inputs) {
    content = <FormulaCell id={id} value={value} computed={computed} inputs={inputs} />;
  } else {
    content = <span className="text-sm font-medium text-gray-900">{value}</span>;
  }
  return (
    <div className="flex items-center justify-between py-1 border-b border-gray-100">
      <span className={`text-xs ${muted ? "text-gray-400" : "text-gray-600"}`}>{label}</span>
      {content}
    </div>
  );
}

export default function CreativeFinanceCalculator() {
  const [inputs, setInputs] = useState({
    address: "123 Main St.",
    purchasePrice: 175000,
    downPayment: 10000,
    commissions: 5250,
    closingFees: 3500,
    improvements: 10000,
    other: 0,
    privateLoan2: 0,
    interestRate1: 5.0,
    termYears1: 30,
    balloonYears1: 5,
    interestRate2: 0,
    termYears2: 0,
    grossRent: 2500,
    mgmtPct: 10,
    mgmtFee: 250,
    maintRes: 125,
    insurance: 80,
    propTax: 128.58,
    vacancyRes: 120,
    maintPct: 5,
    insurancePct: 3,
    propTaxPct: 5,
    vacancyPct: 5,
    gasCost: 0,
    electricCost: 0,
    waterCost: 0,
    landscapingCost: 0,
    cleaningCost: 0,
    cableCost: 0,
    poolCost: 0,
    hoaCost: 0,
    regCost: 0,
    advCost: 0,
    otherExpense: 0,
    miscExpense: 50,
    miscPct: 2,
    asIsValue: 210000,
    arvValue: 275000,
    marketCapRate: 7.0,
    landValue: 40000,

    // Unit Mix Inputs (defaults as per previous static example)
    studioUnits: 0,
    studioRate: 0,
    oneBdrmUnits: 0,
    oneBdrmRate: 0,
    twoBdrmUnits: 0,
    twoBdrmRate: 0,
    threeBdrmUnits: 1,
    threeBdrmRate: 2500,
    fourBdrmUnits: 0,
    fourBdrmRate: 0,
    fiveBdrmUnits: 0,
    fiveBdrmRate: 0,
  });

  const set = useCallback((key) => (val) => setInputs((p) => ({ ...p, [key]: val })), []);

  // Derived values (existing ones unchanged)
  const commissions = inputs.commissions;
  const sellerLoan1 = inputs.purchasePrice - inputs.downPayment;

  // Unit mix calculations
  const unitTypes = [
    { label: "studio", unitsKey: "studioUnits", rateKey: "studioRate" },
    { label: "1 bdrm", unitsKey: "oneBdrmUnits", rateKey: "oneBdrmRate" },
    { label: "2 bdrm", unitsKey: "twoBdrmUnits", rateKey: "twoBdrmRate" },
    { label: "3 bdrm", unitsKey: "threeBdrmUnits", rateKey: "threeBdrmRate" },
    { label: "4 bdrm", unitsKey: "fourBdrmUnits", rateKey: "fourBdrmRate" },
    { label: "5 bdrm", unitsKey: "fiveBdrmUnits", rateKey: "fiveBdrmRate" },
  ];

  const unitRows = unitTypes.map(({ label, unitsKey, rateKey }) => {
    const units = inputs[unitsKey] || 0;
    const rate = inputs[rateKey] || 0;
    const total = units * rate;
    return { label, units, rate, total, unitsKey, rateKey };
  });

  const totalUnits = unitRows.reduce((sum, row) => sum + row.units, 0);
  const totalRevenue = unitRows.reduce((sum, row) => sum + row.total, 0);

  const overnightUnitTypes = [
    { label: "studio", unitsKey: "studioUnitsOvernight", adrKey: "studioADR", occKey: "studioOccupancy" },
    { label: "1 bdrm", unitsKey: "oneBdrmUnitsOvernight", adrKey: "oneBdrmADR", occKey: "oneBdrmOccupancy" },
    { label: "2 bdrm", unitsKey: "twoBdrmUnitsOvernight", adrKey: "twoBdrmADR", occKey: "twoBdrmOccupancy" },
    { label: "3 bdrm", unitsKey: "threeBdrmUnitsOvernight", adrKey: "threeBdrmADR", occKey: "threeBdrmOccupancy" },
    { label: "4 bdrm", unitsKey: "fourBdrmUnitsOvernight", adrKey: "fourBdrmADR", occKey: "fourBdrmOccupancy" },
    { label: "5 bdrm", unitsKey: "fiveBdrmUnitsOvernight", adrKey: "fiveBdrmADR", occKey: "fiveBdrmOccupancy" },
  ];

  const overnightRows = overnightUnitTypes.map(({ label, unitsKey, adrKey, occKey }) => {
    const units = inputs[unitsKey] || 0;
    const adr = inputs[adrKey] || 0;
    const occupancy = inputs[occKey] || 0; // percentage 0–100
    const daily = units * adr * (occupancy / 100);
    const monthly = daily * 30; // assuming 30 days
    return { label, units, adr, occupancy, daily, monthly, unitsKey, adrKey, occKey };
  });

  const totalOvernightUnits = overnightRows.reduce((s, r) => s + r.units, 0);
  const totalOvernightMonthly = overnightRows.reduce((s, r) => s + r.monthly, 0);

  // Existing financial calculations (unchanged)
  const mgmtFee = (inputs.grossRent * inputs.mgmtPct) / 100;
  const maintRes = (inputs.grossRent * inputs.maintPct) / 100;
  const insurance = (inputs.grossRent * inputs.insurancePct) / 100;
  const propTax = (inputs.grossRent * inputs.propTaxPct) / 100;
  const vacancyRes = (inputs.grossRent * inputs.vacancyPct) / 100;
  const miscExp = (inputs.grossRent * inputs.miscPct) / 100;
  const totalExpenses = inputs.mgmtFee + inputs.maintRes + inputs.insurance + inputs.propTax + inputs.vacancyRes + inputs.gasCost + inputs.electricCost + inputs.waterCost + inputs.landscapingCost + inputs.cleaningCost + inputs.cableCost + inputs.poolCost + inputs.hoaCost + inputs.regCost + inputs.advCost + inputs.otherExpense + inputs.miscExpense;
  const noi = inputs.grossRent - totalExpenses;
  const annualNoi = noi * 12;
  const expensePct = inputs.grossRent > 0 ? totalExpenses / inputs.grossRent : 0;

  const monthlyPmt1 = sellerLoan1 > 0 && inputs.interestRate1 > 0 && inputs.termYears1 > 0 ? pmt(inputs.interestRate1 / 100, inputs.termYears1, sellerLoan1) : 0;
  const monthlyPmt2 = inputs.privateLoan2 > 0 && inputs.interestRate2 > 0 && inputs.termYears2 > 0 ? inputs.privateLoan2 * (inputs.interestRate2 / 100 / 12) : 0;
  const totalDebtMonthly = monthlyPmt1 + monthlyPmt2;
  const totalDebtAnnual = totalDebtMonthly * 12;

  const monthlyNetIncome = noi - totalDebtMonthly;
  const assignmentFee = (monthlyNetIncome / 100) * 2000;
  const annualNetIncome = monthlyNetIncome * 12;

  const totalAcquisition = inputs.purchasePrice + inputs.commissions + inputs.closingFees + assignmentFee + inputs.improvements + inputs.other;
  const entryFee = inputs.downPayment + commissions + inputs.closingFees + assignmentFee + inputs.improvements + inputs.other - inputs.privateLoan2;

  const purchaseCapRate = totalAcquisition > 0 ? annualNoi / totalAcquisition : 0;
  const marketCapValue = inputs.marketCapRate > 0 ? annualNoi / (inputs.marketCapRate / 100) : 0;
  const cashOnCash = entryFee > 0 ? annualNetIncome / entryFee : 0;
  const depreciation = (totalAcquisition - inputs.landValue) / 27.5;
  const suggestedAssignment = Math.floor(monthlyNetIncome / 100) * 2000;

  const computed = useMemo(
    () => ({
      commissions,
      sellerLoan1,
      totalAcquisition,
      entryFee,
      monthlyPmt1,
      monthlyPmt2,
      totalDebtMonthly,
      totalDebtAnnual,
      mgmtFee,
      maintRes,
      insurance,
      propTax,
      vacancyRes,
      miscExp,
      totalExpenses,
      noi,
      annualNoi,
      purchaseCapRate,
      marketCapValue,
      monthlyNetIncome,
      assignmentFee,
      annualNetIncome,
      cashOnCash,
      depreciation,
      suggestedAssignment,
    }),
    [commissions, sellerLoan1, totalAcquisition, entryFee, monthlyPmt1, monthlyPmt2, totalDebtMonthly, totalDebtAnnual, mgmtFee, maintRes, insurance, propTax, vacancyRes, miscExp, totalExpenses, noi, annualNoi, purchaseCapRate, marketCapValue, monthlyNetIncome, assignmentFee, annualNetIncome, cashOnCash, depreciation, suggestedAssignment],
  );

  const cashFlowColor = monthlyNetIncome >= 0 ? "#0F6E56" : "#A32D2D";

  return (
    <div className="font-sans max-w-4xl mx-auto px-4 pb-8">
      <h2 className="sr-only">Seller Finance Calculator</h2>

      {/* Header */}
      <div className="py-3 px-4 border-b border-gray-200 mb-2">
        <div className="text-base font-semibold text-gray-900 mb-1">Seller Finance Analysis</div>
        <input type="text" value={inputs.address} onChange={(e) => setInputs((p) => ({ ...p, address: e.target.value }))} placeholder="Property address" className="w-full text-xs px-2 py-1 border border-gray-200 rounded-md text-gray-600" />
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT COLUMN */}
        <div className="pr-2">
          <SectionHeader title="Seller Finance Purchase" color="#185FA5" />
          <Input label="Purchase Price" prefix="$" value={inputs.purchasePrice} onChange={set("purchasePrice")} />
          <Input label="Down Payment" prefix="$" value={inputs.downPayment} onChange={set("downPayment")} />
          <LinkedAmountInput label="Commissions" baseValue={inputs.purchasePrice} amount={inputs.commissions} onAmountChange={set("commissions")} step={100} />
          <Input label="Closing Fees" prefix="$" value={inputs.closingFees} onChange={set("closingFees")} />
          <ResultRow label="Assignment Fee" value={fmt(assignmentFee)} id="assignmentFee" computed={computed} inputs={inputs} />
          <Input label="Improvements" prefix="$" value={inputs.improvements} onChange={set("improvements")} />
          <Input label="Other" prefix="$" value={inputs.other} onChange={set("other")} />
          <ResultRow label="Total Acquisition" value={fmt(totalAcquisition)} id="totalAcquisition" computed={computed} inputs={inputs} />
          <ResultRow label="Seller Carry Loan 1" value={fmt(sellerLoan1)} muted />
          <Input label="Private Loan 2" prefix="$" value={inputs.privateLoan2} onChange={set("privateLoan2")} />
          <ResultRow label="Entry Fee (Cash)" value={fmt(entryFee)} id="entryFee" computed={computed} inputs={inputs} />

          <SectionHeader title="Debt Service" color="#3C3489" />
          <div className="text-[11px] font-medium text-gray-600 py-1">Loan 1 — Seller Finance</div>
          <ResultRow label="Loan Amount" value={fmt(sellerLoan1)} muted />
          <Input label="Interest Rate" prefix="%" step={0.25} value={inputs.interestRate1} onChange={set("interestRate1")} />
          <Input label="Term (years)" value={inputs.termYears1} onChange={set("termYears1")} />
          <Input label="Balloon (years)" value={inputs.balloonYears1} onChange={set("balloonYears1")} />
          <ResultRow label="Monthly Pmt (P&I)" value={fmtD(monthlyPmt1)} id="monthlyPmt" computed={computed} inputs={inputs} />

          <div className="text-[11px] font-medium text-gray-600 py-1 pt-2">Loan 2 — Interest Only</div>
          <Input label="Loan Amount" prefix="$" value={inputs.privateLoan2} onChange={set("privateLoan2")} />
          <Input label="Interest Rate" prefix="%" step={0.25} value={inputs.interestRate2} onChange={set("interestRate2")} />
          <Input label="Term (years)" value={inputs.termYears2} onChange={set("termYears2")} />
          <ResultRow label="Monthly Pmt" value={fmtD(monthlyPmt2)} muted />

          <ResultRow label="Total Debt Service / mo" value={fmt(totalDebtMonthly)} id="totalDebtMonthly" computed={computed} inputs={inputs} />
          <ResultRow label="Total Debt Service / yr" value={fmt(totalDebtAnnual)} id="totalDebtAnnual" computed={computed} inputs={inputs} />

          <SectionHeader title="Investment Analysis" color="#3B6D11" />
          <ResultRow label="Cash-on-Cash ROI" value={pct(cashOnCash)} id="cashOnCash" computed={computed} inputs={inputs} />
          <Input label="Land Value" prefix="$" value={inputs.landValue} onChange={set("landValue")} />
          <ResultRow label="Depreciation Write-off" value={fmt(depreciation)} id="depreciation" computed={computed} inputs={inputs} />
        </div>

        {/* RIGHT COLUMN */}
        <div className="pl-2">
          <SectionHeader title="Rent Analysis" color="#3B6D11" />
          <Input label="Gross Rent" prefix="$" value={inputs.grossRent} onChange={set("grossRent")} />
          <LinkedAmountInput label="Management Fee" baseValue={inputs.grossRent} amount={inputs.mgmtFee} onAmountChange={set("mgmtFee")} step={100} />
          <LinkedAmountInput label="Maintenance Reserve" baseValue={inputs.grossRent} amount={inputs.maintRes} onAmountChange={set("maintRes")} step={100} />
          <LinkedAmountInput label="Insurance" baseValue={inputs.grossRent} amount={inputs.insurance} onAmountChange={set("insurance")} step={100} />
          <LinkedAmountInput label="Property Taxes" baseValue={inputs.grossRent} amount={inputs.propTax} onAmountChange={set("propTax")} step={100} />
          <LinkedAmountInput label="Vacancy Reserve" baseValue={inputs.grossRent} amount={inputs.vacancyRes} onAmountChange={set("vacancyRes")} step={100} />
          <LinkedAmountInput label="Gas" baseValue={inputs.grossRent} amount={inputs.gasCost} onAmountChange={set("gasCost")} step={100} />
          <LinkedAmountInput label="Electric" baseValue={inputs.grossRent} amount={inputs.electricCost} onAmountChange={set("electricCost")} step={100} />
          <LinkedAmountInput label="Water/Sewer/Trash" baseValue={inputs.grossRent} amount={inputs.waterCost} onAmountChange={set("waterCost")} step={100} />
          <LinkedAmountInput label="Landscaping/Snow" baseValue={inputs.grossRent} amount={inputs.landscapingCost} onAmountChange={set("landscapingCost")} step={100} />
          <LinkedAmountInput label="Cleaning" baseValue={inputs.grossRent} amount={inputs.cleaningCost} onAmountChange={set("cleaningCost")} step={100} />
          <LinkedAmountInput label="Cable/Internet" baseValue={inputs.grossRent} amount={inputs.cableCost} onAmountChange={set("cableCost")} step={100} />
          <LinkedAmountInput label="Pool/Spa" baseValue={inputs.grossRent} amount={inputs.poolCost} onAmountChange={set("poolCost")} step={100} />
          <LinkedAmountInput label="HOA" baseValue={inputs.grossRent} amount={inputs.hoaCost} onAmountChange={set("hoaCost")} step={100} />
          <LinkedAmountInput label="Registrations" baseValue={inputs.grossRent} amount={inputs.regCost} onAmountChange={set("regCost")} step={100} />
          <LinkedAmountInput label="Advertising" baseValue={inputs.grossRent} amount={inputs.advCost} onAmountChange={set("advCost")} step={100} />
          <LinkedAmountInput label="Other" baseValue={inputs.grossRent} amount={inputs.otherExpense} onAmountChange={set("otherExpense")} step={100} />
          <LinkedAmountInput label="Misc. Expenses" baseValue={inputs.grossRent} amount={inputs.miscExpense} onAmountChange={set("miscExpense")} step={100} />
          <div className="bg-gray-50 rounded-md p-3 mt-3 space-y-2">
            <ResultRow label="Total Monthly Expenses" value={fmtD(totalExpenses)} percentage={expensePct} />
            <ResultRow label="Net Operating Income (NOI) / mo" value={fmtD(noi)} id="noi" computed={computed} inputs={inputs} percentage={inputs.grossRent > 0 ? noi / inputs.grossRent : 0} />
            <ResultRow label="Annual NOI" value={fmtD(annualNoi)} id="annualNoi" computed={computed} inputs={inputs} />
          </div>

          <SectionHeader title="Value Analysis" color="#854F0B" />
          <ResultRow label="Purchase Cap Rate" value={pct(purchaseCapRate)} id="purchaseCapRate" computed={computed} inputs={inputs} />
          <div className="flex items-center justify-between py-1 border-b border-gray-100">
            <label className="text-xs text-gray-600">Market Cap Rate %</label>
            <input type="number" value={inputs.marketCapRate} step={0.25} onChange={(e) => set("marketCapRate")(parseFloat(e.target.value) || 0)} className="w-[60px] text-right text-xs py-1 px-2 border border-gray-200 rounded-md" />
          </div>
          <ResultRow label="Market Cap Value" value={fmt(marketCapValue)} id="marketCapValue" computed={computed} inputs={inputs} />
          <Input label="AS-IS Value" prefix="$" value={inputs.asIsValue} onChange={set("asIsValue")} />
          <Input label="ARV Value" prefix="$" value={inputs.arvValue} onChange={set("arvValue")} />

          <SectionHeader title="Cashflow Analysis" color={cashFlowColor} />
          <div className="mt-2 bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="grid grid-cols-3 bg-gray-100 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
              <div className="px-3 py-2 text-left"></div>
              <div className="px-3 py-2 text-center">Monthly</div>
              <div className="px-3 py-2 text-center">Annually</div>
            </div>
            <div className="grid grid-cols-3 border-b border-gray-100">
              <div className="px-3 py-1.5 text-xs text-gray-600">Net Operating Income</div>
              <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmtD(noi)}</div>
              <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmtD(annualNoi)}</div>
            </div>
            <div className="grid grid-cols-3 border-b border-gray-100">
              <div className="px-3 py-1.5 text-xs text-gray-600">Debt Service</div>
              <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmtD(totalDebtMonthly)}</div>
              <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmtD(totalDebtAnnual)}</div>
            </div>
            <div className={`grid grid-cols-3 ${monthlyNetIncome >= 0 ? "bg-green-50" : "bg-red-50"}`}>
              <div className={`px-3 py-1.5 text-xs font-medium ${monthlyNetIncome >= 0 ? "text-green-800" : "text-red-800"}`}>Net Income</div>
              <div className={`px-3 py-1.5 text-center text-xs font-bold ${monthlyNetIncome >= 0 ? "text-green-800" : "text-red-800"}`}>{fmtD(monthlyNetIncome)}</div>
              <div className={`px-3 py-1.5 text-center text-xs font-bold ${monthlyNetIncome >= 0 ? "text-green-800" : "text-red-800"}`}>{fmtD(annualNetIncome)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* UNIT MIX LONG-TERM */}
      <SectionHeader title="UNIT MIX LONG-TERM" color="#854F0B" />
      <div className="mt-2 bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-4 bg-gray-100 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
          <div className="px-3 py-2 text-center">Mix</div>
          <div className="px-3 py-2 text-center">Unit</div>
          <div className="px-3 py-2 text-center">Rate</div>
          <div className="px-3 py-2 text-center">Month</div>
        </div>
        {unitRows.map((row) => (
          <div key={row.label} className="grid grid-cols-4 border-b border-gray-100 items-center">
            <div className="px-3 py-1.5 text-xs text-gray-600 capitalize">{row.label}</div>
            <div className="px-3 py-1.5 text-center">
              <input type="number" value={row.units} min={0} step={1} onChange={(e) => set(row.unitsKey)(parseInt(e.target.value) || 0)} className="w-12 text-xs text-center border border-gray-200 rounded" />
            </div>
            <div className="px-3 py-1.5 text-center">
              <input type="number" value={row.rate} min={0} step={100} onChange={(e) => set(row.rateKey)(parseFloat(e.target.value) || 0)} className="w-20 text-xs text-center border border-gray-200 rounded" />
            </div>
            <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmt(row.total)}</div>
          </div>
        ))}
        <div className="grid grid-cols-4 border-b border-gray-100 bg-gray-50">
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-700">TOTAL</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">{totalUnits}</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">—</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">{fmt(totalRevenue)}</div>
        </div>
      </div>

      <SectionHeader title="UNIT MIX OVERNIGHT" color="#6B4E1B" />
      <div className="mt-2 bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-6 bg-gray-100 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
          <div className="px-3 py-2 text-center">Mix</div>
          <div className="px-3 py-2 text-center">Units</div>
          <div className="px-3 py-2 text-center">ADR</div>
          <div className="px-3 py-2 text-center">Occupancy</div>
          <div className="px-3 py-2 text-center">Daily</div>
          <div className="px-3 py-2 text-center">Monthly</div>
        </div>
        {overnightRows.map((row) => (
          <div key={row.label} className="grid grid-cols-6 border-b border-gray-100 items-center">
            <div className="px-3 py-1.5 text-xs text-gray-600 capitalize">{row.label}</div>
            <div className="px-3 py-1.5 text-center">
              <input type="number" value={row.units} min={0} step={1} onChange={(e) => set(row.unitsKey)(parseInt(e.target.value) || 0)} className="w-12 text-xs text-center border border-gray-200 rounded" />
            </div>
            <div className="px-3 py-1.5 text-center">
              <input type="number" value={row.adr} min={0} step={10} onChange={(e) => set(row.adrKey)(parseFloat(e.target.value) || 0)} className="w-16 text-xs text-center border border-gray-200 rounded" />
            </div>
            <div className="px-3 py-1.5 text-center">
              <input type="number" value={row.occupancy} min={0} max={100} step={1} onChange={(e) => set(row.occKey)(parseFloat(e.target.value) || 0)} className="w-14 text-xs text-center border border-gray-200 rounded" />
              <span className="text-xs text-gray-500 ml-0.5">%</span>
            </div>
            <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmtD(row.daily)}</div>
            <div className="px-3 py-1.5 text-center text-xs font-medium text-gray-900">{fmt(row.monthly)}</div>
          </div>
        ))}
        {/* Totals Row */}
        <div className="grid grid-cols-6 border-b border-gray-100 bg-gray-50">
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-700">TOTAL</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">{totalOvernightUnits}</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">—</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">—</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">—</div>
          <div className="px-3 py-1.5 text-center text-xs font-semibold text-gray-900">{fmt(totalOvernightMonthly)}</div>
        </div>
      </div>
    </div>
  );
}
